import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { AssetPartsTable } from "./asset-parts-table";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	linkWorkOrderParts,
	unLinkWorkOrderParts,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { WorkOrderPart } from "../../../../models/WorkOrderPart";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	WorkOrderId: number | null;
	WorkOrderName: string;
	Title: string;
	AssetName: string;
}

export const AssetPartsPicker: React.FC<Props> = ({
	show,
	onHide,
	WorkOrderId,
	WorkOrderName,
	Title,
	AssetName,
}) => {
	const {
		state: { workOrderPartsList },
	} = useWorkOrderModalsContext();

	const [selectedParts, setSelectedParts] = useState<WorkOrderPart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedParts(
				workOrderPartsList.filter((part) => part.IsAssetResource),
			);
		}
	}, [show, workOrderPartsList]);

	const handleSelectionChanged = (newSelectedParts: WorkOrderPart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const getNewlyAddedParts = (
		currentSelected: WorkOrderPart[],
		originalList: WorkOrderPart[],
	): WorkOrderPart[] => {
		return currentSelected.filter(
			(selectedPart) =>
				!originalList.some(
					(existingPart) =>
						existingPart.PartName === selectedPart.PartName &&
						existingPart.IsAssetResource,
				),
		);
	};

	const getRemovedParts = (
		currentSelected: WorkOrderPart[],
		originalList: WorkOrderPart[],
	): WorkOrderPart[] => {
		return originalList.filter(
			(originalPart) =>
				originalPart.IsAssetResource &&
				!currentSelected.some(
					(selectedPart) => selectedPart.PartName === originalPart.PartName,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedParts = getNewlyAddedParts(
			selectedParts,
			workOrderPartsList,
		);
		const removedParts = getRemovedParts(selectedParts, workOrderPartsList);

		if (newlyAddedParts.length > 0) {
			const newAssetParts = newlyAddedParts.map((part) => ({
				PartName: part.PartName,
				IsAssetResource: true,
			}));
			await linkWorkOrderParts(WorkOrderId as number, newAssetParts as any);
			onHide(true);
		} else onHide(false);

		if (removedParts.length > 0) {
			const assetPartsToRemoved = removedParts.map((part) => ({
				PartName: part.PartName,
				IsAssetResource: true,
			}));
			await unLinkWorkOrderParts(
				WorkOrderId as number,
				assetPartsToRemoved as any,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						PARTS FROM ASSET
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Work Order:</span>
							<span style={{ marginLeft: "4px" }}>{WorkOrderName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetPartsTable
						workOrderParts={workOrderPartsList.filter(
							(part) => part.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button variant="contained" color="primary" onClick={handleSave}>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
